import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Menu, X, ChevronDown, Home, Building2, Tractor, Package, Landmark, 
  Car, FlaskConical, Wrench, Leaf, FileCheck, Award, BarChart3, Wifi,
  Briefcase, Users, CreditCard, Banknote, TrendingUp, Receipt,
  HelpCircle, Shield, Settings, Info, Target, Heart, Newspaper, Calendar,
  Calculator as CalcIcon
} from 'lucide-react';

const Navigation = ({ isScrolled }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const menuTimeoutRef = useRef(null);

  // Función para navegar y hacer scroll al top
  const handleNavigate = (route) => {
    navigate(route);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setMobileMenuOpen(false);
    setActiveMegaMenu(null);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
      setActiveMegaMenu(null);
    }
  };

  const handleMouseEnter = (menu) => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
    }
    setActiveMegaMenu(menu);
  };

  const handleMouseLeave = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setActiveMegaMenu(null);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (menuTimeoutRef.current) {
        clearTimeout(menuTimeoutRef.current);
      }
    };
  }, []);

  // Menu structure con rutas dedicadas
  const menuStructure = {
    productos: {
      title: 'Productos',
      items: [
        { name: 'Residenciales', icon: Home, route: '/servicios/residenciales' },
        { name: 'Empresas e Industrias', icon: Building2, route: '/servicios/empresas' },
        { name: 'Soluciones Rurales y Agro', icon: Tractor, route: '/servicios/rural-agro' },
        { name: 'Productos Solares', icon: Package, route: '/productos/catalogo' },
        { name: 'Movilidad Eléctrica', icon: Car, route: '/productos/movilidad-electrica' },
        { name: 'Laboratorio de Innovación', icon: FlaskConical, route: '/productos/laboratorio' },
      ]
    },
    servicios: {
      title: 'Servicios',
      items: [
        { name: 'Instalación', icon: Wrench, route: '/servicios/instalacion' },
        { name: 'Soluciones Ecológicas', icon: Leaf, route: '/servicios/ecologicas' },
        { name: 'Gestoría y Trámites de Inyección a Red', icon: FileCheck, route: '/servicios/gestoria' },
        { name: 'Certificación de "Huella Cero" para Negocios', icon: Award, route: '/servicios/huella-cero' },
        { name: 'Auditoría Energética', icon: BarChart3, route: '/servicios/auditoria' },
        { name: 'Internet Satelital', icon: Wifi, route: '/servicios/internet' },
      ]
    },
    proyectos: {
      title: 'Proyectos',
      items: [
        { name: 'Privados', icon: Briefcase, route: '/proyectos/privados' },
        { name: 'Acción Social', icon: Users, route: '/proyectos/accion-social' },
      ]
    },
    financiamiento: {
      title: 'Financiamiento',
      items: [
        { name: 'Financiación Propia', icon: CreditCard, route: '/financiamiento/propia' },
        { name: 'Planes a Cuotas con Bancos Afiliados', icon: Banknote, route: '/financiamiento/bancos' },
        // { name: 'Créditos Verdes', icon: TrendingUp, route: '/financiamiento/creditos-verdes' }, // Oculto temporalmente
        // { name: 'Beneficios Fiscales e Impuestos', icon: Receipt, route: '/financiamiento/beneficios' }, // Oculto temporalmente
      ]
    },
    soporte: {
      title: 'Soporte',
      items: [
        { name: 'Preguntas Frecuentes', icon: HelpCircle, route: '/soporte/faq' },
        { name: 'Garantías', icon: Shield, route: '/soporte/garantias' },
        { name: 'Servicio Técnico', icon: Settings, route: '/soporte/servicio-tecnico' },
      ]
    },
    nosotros: {
      title: 'Nosotros',
      items: [
        { name: 'Soporte', icon: HelpCircle, route: '/nosotros/soporte' },
        { name: 'Nuestra Historia', icon: Info, route: '/nosotros/historia' },
        { name: 'Misión y Visión', icon: Target, route: '/nosotros/mision-vision' },
        { name: 'Sustentabilidad', icon: Leaf, route: '/nosotros/sustentabilidad' },
        { name: 'Trabaja con Nosotros', icon: Users, route: '/nosotros/trabaja' },
        { name: 'Noticias', icon: Newspaper, route: '/nosotros/noticias' },
        { name: 'Eventos', icon: Calendar, route: '/nosotros/eventos' },
      ]
    }
  };

  // Render Mega Menu - Diseño ULTRA CONTRASTANTE Y FUTURISTA
  const renderMegaMenu = (menuKey) => {
    const menu = menuStructure[menuKey];
    if (!menu) return null;

    const isMegaMenu = menuKey === 'productos' || menuKey === 'servicios';
    const cols = isMegaMenu ? 'grid-cols-3 lg:grid-cols-4' : 'grid-cols-2 lg:grid-cols-3';

    return (
      <div
        className={`absolute top-full left-0 right-0 bg-zinc-900 backdrop-blur-xl border-t-4 border-[#90EE90] shadow-[0_20px_60px_rgba(0,0,0,0.4)] transition-all duration-500 ${
          activeMegaMenu === menuKey 
            ? 'opacity-100 visible translate-y-0' 
            : 'opacity-0 invisible -translate-y-8 pointer-events-none'
        }`}
        onMouseEnter={() => handleMouseEnter(menuKey)}
        onMouseLeave={handleMouseLeave}
      >
        {/* Animated glowing top border */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#90EE90] to-transparent animate-pulse"></div>
        
        {/* Subtle grid pattern background */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(#90EE90 1px, transparent 1px), linear-gradient(90deg, #90EE90 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-8">
          {/* Menu Title - Futurista */}
          <div className="mb-6 pb-4 border-b border-[#90EE90]/30">
            <h3 className="text-sm uppercase tracking-[0.3em] text-[#90EE90] font-bold flex items-center gap-3">
              <span className="w-8 h-0.5 bg-gradient-to-r from-[#90EE90] to-transparent"></span>
              {menu.title}
              <span className="w-8 h-0.5 bg-gradient-to-l from-[#90EE90] to-transparent"></span>
            </h3>
          </div>

          <div className={`grid ${cols} gap-4`}>
            {menu.items.map((item, idx) => {
              const Icon = item.icon;
              return (
                <button
                  key={idx}
                  onClick={() => handleNavigate(item.route)}
                  className="relative group p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border-2 border-zinc-700/50 hover:border-[#90EE90] transition-all duration-300 text-left overflow-hidden backdrop-blur-sm w-full"
                  data-testid={`submenu-${menuKey}-${idx}`}
                >
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#90EE90]/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Corner accent */}
                  <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-[#90EE90]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-bl-full"></div>
                  
                  <div className="relative flex items-center gap-4">
                    {/* Icon with dramatic glow */}
                    <div className="relative flex-shrink-0">
                      {/* Outer glow ring */}
                      <div className="absolute inset-0 bg-[#90EE90] rounded-xl blur-xl opacity-0 group-hover:opacity-60 transition-all duration-500 scale-150"></div>
                      
                      {/* Icon container */}
                      <div className="relative bg-gradient-to-br from-[#90EE90] to-[#7CDF7C] p-3 rounded-xl group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-[#90EE90]/50">
                        <Icon className="text-zinc-900" size={24} strokeWidth={2.5} />
                      </div>
                    </div>
                    
                    {/* Text */}
                    <div className="flex-1">
                      <span className="text-sm font-bold text-white group-hover:text-[#90EE90] transition-colors leading-tight block">
                        {item.name}
                      </span>
                    </div>

                    {/* Arrow indicator */}
                    <svg 
                      className="w-5 h-5 text-zinc-600 group-hover:text-[#90EE90] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 flex-shrink-0" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>

                  {/* Bottom glow line */}
                  <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-[#90EE90] to-[#7CDF7C] group-hover:w-full transition-all duration-500"></div>
                </button>
              );
            })}
          </div>

          {/* CTA Premium */}
          {isMegaMenu && (
            <div className="mt-8 pt-6 border-t border-[#90EE90]/30">
              <a
                href="https://wa.me/595991344956?text=Hola!%20Necesito%20asesor%C3%ADa%20personalizada%20sobre%20energ%C3%ADa%20solar."
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#90EE90] to-[#7CDF7C] rounded-lg hover:shadow-lg hover:shadow-[#90EE90]/50 transition-all duration-300 hover:scale-105"
              >
                <span className="text-sm font-bold text-zinc-900">¿Necesitas asesoría personalizada?</span>
                <svg className="w-4 h-4 text-zinc-900 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <button 
            onClick={() => handleNavigate('/')}
            className="flex items-center cursor-pointer"
            data-testid="logo-button"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_9f79b292-d9d5-4e5e-9601-bb175f5ef285/artifacts/uwzl557l_5.png"
              alt="Zenex Logo"
              className="h-8 md:h-10"
            />
          </button>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-6">
            {Object.keys(menuStructure).map((menuKey) => (
              <div
                key={menuKey}
                className="relative"
                onMouseEnter={() => handleMouseEnter(menuKey)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className="flex items-center gap-1 text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors py-2"
                  data-testid={`nav-${menuKey}`}
                >
                  {menuStructure[menuKey].title}
                  <ChevronDown size={16} className={`transition-transform duration-200 ${
                    activeMegaMenu === menuKey ? 'rotate-180' : ''
                  }`} />
                </button>
              </div>
            ))}

            {/* Calculadora Button - Destacado */}
            <button
              onClick={() => handleNavigate('/calculadora')}
              className="relative bg-gradient-to-r from-[#90EE90] to-[#7CDF7C] text-[#14532D] hover:from-[#7CDF7C] hover:to-[#6ACE6A] px-6 py-3 font-bold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
              data-testid="nav-calculator-cta"
            >
              <CalcIcon size={18} />
              <span>Calculadora Solar</span>
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full animate-pulse">
                Nuevo
              </span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-zinc-900"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mega Menus - Desktop */}
        <div className="hidden lg:block">
          {Object.keys(menuStructure).map((menuKey) => renderMegaMenu(menuKey))}
        </div>
      </div>
    </nav>

    {/* Mobile Menu - Diseño Innovador Full Screen - FUERA del nav */}
      <div 
        className={`lg:hidden fixed inset-0 z-[100] transition-all duration-500 ${
          mobileMenuOpen ? 'visible' : 'invisible'
        }`}
        data-testid="mobile-menu"
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-500 ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMobileMenuOpen(false)}
        />
        
        {/* Menu Panel - Slide from bottom */}
        <div 
          className={`absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl transition-transform duration-500 ease-out max-h-[85vh] overflow-hidden ${
            mobileMenuOpen ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1.5 bg-zinc-300 rounded-full"></div>
          </div>
          
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-zinc-100">
            <span className="text-lg font-bold text-zinc-900">Menú</span>
            <button 
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 hover:bg-zinc-100 rounded-full transition-colors"
            >
              <X size={24} className="text-zinc-600" />
            </button>
          </div>
          
          {/* Scrollable Content */}
          <div className="overflow-y-auto max-h-[calc(85vh-120px)] pb-8">
            {/* Calculadora CTA - Hero */}
            <div className="px-4 pt-4">
              <button
                onClick={() => handleNavigate('/calculadora')}
                className="relative block w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-5 rounded-2xl shadow-lg overflow-hidden group text-left"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                <div className="relative flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-xl">
                    <CalcIcon size={28} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <span className="block text-lg font-bold">Calculadora Solar</span>
                    <span className="text-sm text-white/80">Calculá tu ahorro gratis</span>
                  </div>
                  <div className="bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full">
                    GRATIS
                  </div>
                </div>
              </button>
            </div>
            
            {/* Quick Access Grid - Main Categories */}
            <div className="px-4 pt-6">
              <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3 px-2">Acceso rápido</p>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { name: 'Residencial', icon: Home, route: '/servicios/residenciales' },
                  { name: 'Empresas', icon: Building2, route: '/servicios/empresas' },
                  { name: 'Rural', icon: Tractor, route: '/servicios/rural-agro' },
                  { name: 'Productos', icon: Package, route: '/productos/catalogo' },
                  { name: 'Financiamiento', icon: CreditCard, route: '/financiamiento/bancos' },
                  { name: 'Soporte', icon: HelpCircle, route: '/soporte/faq' },
                ].map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={idx}
                      onClick={() => handleNavigate(item.route)}
                      className="group flex flex-col items-center gap-3 p-4 rounded-2xl transition-all duration-300 active:scale-95 bg-gradient-to-br from-white to-zinc-50 border border-[#90EE90]/30 hover:border-[#90EE90] hover:shadow-[0_0_20px_rgba(144,238,144,0.25)]"
                    >
                      {/* Contenedor del icono con efecto neón claro */}
                      <div className="relative">
                        {/* Anillo exterior con gradiente */}
                        <div className="absolute -inset-1 rounded-xl bg-gradient-to-br from-[#90EE90]/30 via-transparent to-[#14532D]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
                        
                        {/* Círculo principal */}
                        <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-[#90EE90]/10 to-[#90EE90]/20 border border-[#90EE90]/40 group-hover:border-[#90EE90] flex items-center justify-center transition-all duration-300 group-hover:shadow-[0_0_15px_rgba(144,238,144,0.4)] group-hover:bg-gradient-to-br group-hover:from-[#90EE90]/20 group-hover:to-[#90EE90]/30">
                          {/* Línea de energía superior */}
                          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-[2px] bg-gradient-to-r from-transparent via-[#90EE90]/60 to-transparent group-hover:via-[#14532D] transition-all duration-300"></div>
                          
                          {/* Icono */}
                          <Icon size={22} className="text-[#14532D]/70 group-hover:text-[#14532D] transition-colors duration-300" />
                          
                          {/* Línea de energía inferior */}
                          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-[2px] bg-gradient-to-r from-transparent via-[#90EE90]/60 to-transparent group-hover:via-[#14532D] transition-all duration-300"></div>
                        </div>
                        
                        {/* Punto de energía */}
                        <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#90EE90] opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-[0_0_6px_rgba(144,238,144,0.8)]"></div>
                      </div>
                      
                      <span className="text-[11px] font-medium text-zinc-600 group-hover:text-[#14532D] transition-colors duration-300 text-center">{item.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Full Menu Sections */}
            <div className="px-4 pt-6 space-y-4">
              <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1 px-2">Explorar</p>
              
              {Object.keys(menuStructure).map((menuKey) => {
                const menu = menuStructure[menuKey];
                const isOpen = activeAccordion === menuKey;
                
                // Icon mapping for sections
                const sectionIcons = {
                  productos: Package,
                  servicios: Wrench,
                  proyectos: Briefcase,
                  financiamiento: Banknote,
                  soporte: Shield,
                  nosotros: Users
                };
                const SectionIcon = sectionIcons[menuKey] || Package;
                
                return (
                  <div key={menuKey} className="bg-zinc-50 rounded-2xl overflow-hidden">
                    <button
                      onClick={() => setActiveAccordion(isOpen ? null : menuKey)}
                      className="flex items-center gap-4 w-full px-4 py-4 text-left transition-colors"
                    >
                      <div className="bg-emerald-100 p-2.5 rounded-xl">
                        <SectionIcon size={20} className="text-emerald-600" />
                      </div>
                      <span className="flex-1 font-semibold text-zinc-800">{menu.title}</span>
                      <div className={`p-1 rounded-full transition-all duration-300 ${isOpen ? 'bg-emerald-500 rotate-180' : 'bg-zinc-200'}`}>
                        <ChevronDown size={18} className={isOpen ? 'text-white' : 'text-zinc-500'} />
                      </div>
                    </button>
                    
                    <div 
                      className={`transition-all duration-300 ease-in-out ${
                        isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                      } overflow-hidden`}
                    >
                      <div className="px-4 pb-4 grid grid-cols-2 gap-2">
                        {menu.items.map((item, idx) => {
                          const Icon = item.icon;
                          return (
                            <button
                              key={idx}
                              onClick={() => handleNavigate(item.route)}
                              className="flex items-center gap-3 px-3 py-3 bg-white rounded-xl hover:shadow-md transition-all active:scale-98 border border-zinc-100 text-left"
                            >
                              <Icon size={18} className="text-emerald-500 flex-shrink-0" />
                              <span className="text-xs font-medium text-zinc-700 leading-tight">
                                {item.name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Contact CTA */}
            <div className="px-4 pt-6">
              <a
                href="https://wa.me/595991344956"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full bg-zinc-900 text-white px-6 py-4 rounded-2xl font-semibold hover:bg-zinc-800 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Hablar con un Asesor
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;